import { FacilityAndSpaceFeatures } from "../Variables/Cortex/CortexFacilityFeatures.Data";
import { FACILITY_INFO, FacilityInfoDef, FacilityInfoKey } from "../Variables/Cortex/CortexFacilityInfo.Data";

/** Single source of truth for what the scraper targets — add a field here by flipping `scrapable` on its FacilityInfoDef. */
export function getScrapableFacilityInfoDefs(): FacilityInfoDef[] {
  return FACILITY_INFO.filter((f) => f.scrapable);
}

/** A scraped value with its confidence and the snippet that justified it. */
export interface ScraperFieldValue {
  value: string | null;
  /** 0..1; 1 for deterministic (JSON-LD) hits, the model's number otherwise. */
  confidence: number;
  /** Verbatim snippet that justified the value; "" for deterministic hits. */
  citation: string;
}

/** Scalar facts found on the page, keyed by FacilityInfoKey — only scrapable keys are ever populated. */
export interface ScraperExtraction {
  fields: Partial<Record<FacilityInfoKey, ScraperFieldValue>>;
  /** Features the page explicitly states; each with its snippet and confidence. */
  features: { key: FacilityAndSpaceFeatures; citation: string; confidence: number }[];
  /** Free-text hours per type, verbatim, for mapping to the matching schedule slots. */
  hours?: ScraperHours;
}

/** Free-text hours by type as stated on the page; each parsed into its schedule separately. */
export interface ScraperHours {
  officeHours?: ScraperFieldValue;
  gateAccess?: ScraperFieldValue;
  callCenter?: ScraperFieldValue;
  holidays?: ScraperFieldValue;
}

export function emptyScraperExtraction(): ScraperExtraction {
  const fields: Partial<Record<FacilityInfoKey, ScraperFieldValue>> = {};
  for (const def of getScrapableFacilityInfoDefs()) fields[def.key] = { value: null, confidence: 0, citation: "" };
  return { fields, features: [] };
}

/** One scalar field, resolved to its Cortex var target and a value. */
export interface ScraperPreviewField {
  facilityInfoKey: FacilityInfoKey;
  cortexVarKey: FacilityInfoDef["cortexVarKey"];
  label: string;
  value: string;
}

export interface ScraperPreviewFeature {
  key: FacilityAndSpaceFeatures;
  label: string;
  /** Verbatim snippet from the page that justified checking this feature. */
  citation: string;
}

/** Preview only — nothing written; autoApply is JSON-LD-backed, needsReview is LLM-only, notFound is neither. */
export interface ScraperPreviewResult {
  autoApply: ScraperPreviewField[];
  needsReview: ScraperPreviewField[];
  notFound: FacilityInfoKey[];
  features: ScraperPreviewFeature[];
  /** Set when the page couldn't be fetched at all; buckets above are empty. */
  fetchError?: string;
  /** True if the page was long enough that some content was never sent to Claude — notFound may be incomplete, not confirmed absent. */
  truncated: boolean;
  /** Photo URL scraped from og:image, twitter:image, or JSON-LD image — null if none found. */
  photoUrl: string | null;
  /** The persisted `ingested_source` row id for this scrape; null if the row couldn't be written. */
  id: number | null;
  /** An existing location matching this URL/address — suggest editing it instead of duplicating. */
  duplicateOf?: DuplicateLocationMatch | null;
}

/** A pre-existing location a scrape appears to duplicate. */
export interface DuplicateLocationMatch {
  locationId: number;
  name: string;
  groupId: number;
  /** Which signal matched, for the "Based on its {URL/address}" copy. */
  matchedOn: "URL" | "address";
}

/** A scrape the operator started but never signed off — surfaced so they can pick it back up. */
export interface UnfinishedScrape {
  id: number;
  /** The scraped URL, shown when no name was extracted. */
  source: string;
  /** Scraped facility name/address, for a friendlier label than the URL. */
  name: string | null;
  address: string | null;
  createdAt: string;
  /** The location created for this scrape, or null if they closed before creating one. */
  locationId: number | null;
  /** Onboarding completion 0-100 (null when no location exists yet to measure). */
  percentComplete: number | null;
  /** Policy scan still running vs. done. */
  scanning: boolean;
}
